import * as React from 'react';
import { useForm } from 'react-hook-form';
import {
  useTranslations,
  withTranslationsProvider,
} from '@narvar/nth-hook-translations';
import { Column } from '@narvar/nth-grid';
import Button, { ButtonType } from '@narvar/nth-block-button';
import { SemibigHeader } from '@narvar/nth-bit-header';
import Paragraph from '@narvar/nth-bit-paragraph';
import CustomComponentContext from '../CustomComponentContext';
import RegisteredInput from '../form-fields/RegisteredInput';
import SelectList from '../form-fields/RegisteredSelectList';
import TextLink from '../TextLink';
import { FormRow, FormColumn, ActionsRow } from '../form-layout';
import states from './states';
import {
  useDeliveryInstructionsContext,
  UI_STATE,
  DEFAULT_DELIVERY_INSTRUCTIONS_CONTEXT,
} from '../../hooks/useDeliveryInstructionsState';
import { verifyAddress } from '../../api/eligibility';
import PanelHeaderText from '../PanelHeaderText';
import ErrorAlert from '../ErrorAlert';
import StepHeader from '../StepHeader';

enum InputNames {
  firstName = 'firstName',
  lastName = 'lastName',
  addressLine1 = 'addressLine1',
  addressLine2 = 'addressLine2',
  city = 'city',
  stateCode = 'stateCode',
  postalCode = 'postalCode',
  countryCode = 'countryCode',
}

type FormData = Record<InputNames, string>;

interface AddressFormProps {
  featureName?: string;
}

const AddressForm: React.FunctionComponent<AddressFormProps> = ({
  featureName,
}) => {
  const [showVerificationError, setShowVerificationError] =
    React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const { state, track } = React.useContext(CustomComponentContext);
  const { update: updateUIState } = useDeliveryInstructionsContext();
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormData>();

  const {
    deliveryInstructionsFirstName,
    deliveryInstructionsLastName,
    deliveryInstructionsStep1Text,
    deliveryInstructionsVerifyAddressDescription,
    deliveryInstructionsAddressLine1,
    deliveryInstructionsAddressLine2,
    deliveryInstructionsCity,
    deliveryInstructionsState,
    deliveryInstructionsPostalCode,
    deliveryInstructionsCountry,
    deliveryInstructionsVerifyAddressHeader,
    deliveryInstructionsVerifyButtonText,
    deliveryInstructionsCancelButtonText,
    deliveryInstructionsRequiredFieldErrorText,
    deliveryInstructionsVerifyErrorText,
  } = useTranslations();

  // Reset the form when the component unmounts. There's no flow
  // to return back to the form with values remaining in the fields,
  // So once we're done with this panel, reset it right away.
  React.useEffect(() => reset, []);

  const onSubmit = handleSubmit(async (address) => {
    if (submitting) return;

    const { carrier, trackingNumbers, retailerMoniker } =
      state?.pageContext || {};
    const trackingNumber = trackingNumbers ? trackingNumbers[0] : undefined;

    if (!(carrier && trackingNumber)) return;

    setSubmitting(true);
    setShowVerificationError(false);
    track({
      eventType: 'click',
      clickSubType: 'verify',
    });

    try {
      const {
        addressVerified,
        leaveAtOptions,
        leaveAtValue,
        registerAccount,
        asrSignatureRequired,
      } = await verifyAddress({
        carrier,
        trackingNumber,
        retailerMoniker,
        addressVerification: address,
      });
      if (addressVerified) {
        updateUIState({
          deliveryInstructionsUIState: UI_STATE.DELIVERY_INSTRUCTIONS,
          stepNavigationChange: true,
          verifiedAddress: address,
          leaveAtOptions,
          leaveAtValue,
          registerAccount,
          asrSignatureRequired,
        });
      } else {
        setShowVerificationError(true);
        setSubmitting(false);
      }
    } catch {
      setSubmitting(false);
    }
  });

  const handleCancel: React.MouseEventHandler<HTMLButtonElement> = () => {
    updateUIState({
      ...DEFAULT_DELIVERY_INSTRUCTIONS_CONTEXT,
      stepNavigationChange: true,
    });
  };

  return (
    <div style={{ padding: '0.75rem ' }}>
      <StepHeader>
        <PanelHeaderText>{deliveryInstructionsStep1Text}</PanelHeaderText>
        <SemibigHeader as="h2">
          {deliveryInstructionsVerifyAddressHeader}
        </SemibigHeader>
      </StepHeader>
      <Paragraph containerCustomStyle={{ margin: '1rem 0 1.5rem 0' }}>
        {deliveryInstructionsVerifyAddressDescription}
      </Paragraph>
      <ErrorAlert
        show={showVerificationError}
        focusOnShow
        error
        dismissible={false}
        role="alert"
        timeout={0}
        s={{
          padding: '1rem',
          margin: '0 0 2rem 0',
          borderRadius: '.25rem',
        }}
      >
        <Paragraph containerCustomStyle={{ margin: 0, color: '#000' }}>
          {deliveryInstructionsVerifyErrorText}
        </Paragraph>
      </ErrorAlert>
      <form
        noValidate
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={onSubmit}
      >
        <FormRow>
          <FormColumn sm-columns={6}>
            <RegisteredInput
              featureName={featureName}
              id={InputNames.firstName}
              label={deliveryInstructionsFirstName || ''}
              animateLabel
              errorMessage={errors.firstName && errors.firstName.message}
              value={watch(InputNames.firstName)}
              register={register(InputNames.firstName, {
                required: {
                  value: true,
                  message: deliveryInstructionsRequiredFieldErrorText || '',
                },
              })}
            />
          </FormColumn>
          <FormColumn sm-columns={6}>
            <RegisteredInput
              featureName={featureName}
              id={InputNames.lastName}
              label={deliveryInstructionsLastName || ''}
              animateLabel
              errorMessage={errors.lastName && errors.lastName.message}
              value={watch(InputNames.lastName)}
              register={register(InputNames.lastName, {
                required: {
                  value: true,
                  message: deliveryInstructionsRequiredFieldErrorText || '',
                },
              })}
            />
          </FormColumn>
        </FormRow>
        <FormRow>
          <FormColumn sm-columns={6}>
            <RegisteredInput
              featureName={featureName}
              id={InputNames.addressLine1}
              label={deliveryInstructionsAddressLine1 || ''}
              animateLabel
              errorMessage={errors.addressLine1 && errors.addressLine1.message}
              value={watch(InputNames.addressLine1)}
              register={register(InputNames.addressLine1, {
                required: {
                  value: true,
                  message: deliveryInstructionsRequiredFieldErrorText || '',
                },
              })}
            />
          </FormColumn>
          <FormColumn sm-columns={6}>
            <RegisteredInput
              featureName={featureName}
              id={InputNames.addressLine2}
              label={deliveryInstructionsAddressLine2 || ''}
              animateLabel
              value={watch(InputNames.addressLine2)}
              register={register(InputNames.addressLine2)}
            />
          </FormColumn>
        </FormRow>
        <FormRow>
          <FormColumn sm-columns={3}>
            <RegisteredInput
              featureName={featureName}
              id={InputNames.city}
              label={deliveryInstructionsCity || ''}
              animateLabel
              errorMessage={errors.city && errors.city.message}
              value={watch(InputNames.city)}
              register={register(InputNames.city, {
                required: {
                  value: true,
                  message: deliveryInstructionsRequiredFieldErrorText || '',
                },
              })}
            />
          </FormColumn>
          <FormColumn sm-columns={3}>
            {/* TODO: Replace values with data from loma */}
            <SelectList
              featureName={featureName}
              id={InputNames.stateCode}
              label={deliveryInstructionsState || ''}
              animateLabel
              errorMessage={errors.stateCode && errors.stateCode.message}
              value={watch(InputNames.stateCode)}
              register={register(InputNames.stateCode, {
                required: {
                  value: true,
                  message: deliveryInstructionsRequiredFieldErrorText || '',
                },
              })}
            >
              {/*
                aria-label removes the following error message,
                but a screen reader doesn't actually read "State" for the option:
                "A control must be associated with a text label        jsx-a11y/control-has-associated-label"

                This doesn't seem to provide a diminished experience though. If we wanted to improve this though,
                we'd need to bring in something like reachUI/Listbox to have better control.
              */}
              <option value="" aria-label={deliveryInstructionsState || ''} />
              {states.map((stateOption) => (
                <option
                  value={stateOption.abbreviation}
                  key={stateOption.abbreviation}
                >
                  {stateOption.name}
                </option>
              ))}
            </SelectList>
          </FormColumn>
          <FormColumn sm-columns={3}>
            <RegisteredInput
              featureName={featureName}
              id={InputNames.postalCode}
              label={deliveryInstructionsPostalCode || ''}
              animateLabel
              errorMessage={errors.postalCode && errors.postalCode.message}
              value={watch(InputNames.postalCode)}
              register={register(InputNames.postalCode, {
                required: {
                  value: true,
                  message: deliveryInstructionsRequiredFieldErrorText || '',
                },
              })}
            />
          </FormColumn>
          <FormColumn sm-columns={3}>
            {/* TODO: Replace values with data from loma */}
            <SelectList
              featureName={featureName}
              id={InputNames.countryCode}
              label={deliveryInstructionsCountry || ''}
              animateLabel
              errorMessage={errors.countryCode && errors.countryCode.message}
              value={watch(InputNames.countryCode)}
              defaultValue="US"
              register={register(InputNames.countryCode, {
                required: {
                  value: true,
                  message: deliveryInstructionsRequiredFieldErrorText || '',
                },
              })}
            >
              {/*
                aria-label removes the following error message,
                but a screen reader doesn't actually read "Country" for the option:
                "A control must be associated with a text label        jsx-a11y/control-has-associated-label"

                This doesn't seem to provide a diminished experience though. If we wanted to improve this though,
                we'd need to bring in something like reachUI/Listbox to have better control.
              */}
              <option value="" aria-label={deliveryInstructionsCountry || ''} />
              <option value="US">United States</option>
            </SelectList>
          </FormColumn>
        </FormRow>
        <ActionsRow>
          <FormColumn sm-columns={3} xs-columns={12}>
            <Button big type={ButtonType.submit} disabled={submitting}>
              {deliveryInstructionsVerifyButtonText}
            </Button>
          </FormColumn>
          <Column sm-columns={9}>
            {/*
              TODO: This should probably use `nth-text-link`, but is not because:
              1) This will need `type` set to button so that it doesn't submit the form. Can't set type on `nth-text-link`
              2) This should probably have some padding on mobile to make it easier to tap. Can't set padding on `nth-text-link`
              3) `nth-text-link` doesn't have theming. It seems the text should be set to the primary button color
            */}
            <TextLink onClick={handleCancel}>
              {deliveryInstructionsCancelButtonText}
            </TextLink>
          </Column>
        </ActionsRow>
      </form>
    </div>
  );
};

export default withTranslationsProvider(AddressForm, {
  translationKeys: [
    'deliveryInstructionsFirstName',
    'deliveryInstructionsLastName',
    'deliveryInstructionsStep1Text',
    'deliveryInstructionsVerifyAddressDescription',
    'deliveryInstructionsAddressLine1',
    'deliveryInstructionsAddressLine2',
    'deliveryInstructionsCity',
    'deliveryInstructionsState',
    'deliveryInstructionsPostalCode',
    'deliveryInstructionsCountry',
    'deliveryInstructionsVerifyAddressHeader',
    'deliveryInstructionsVerifyButtonText',
    'deliveryInstructionsCancelButtonText',
    'deliveryInstructionsRequiredFieldErrorText',
    'deliveryInstructionsVerifyErrorText',
  ],
});
